import * as React from 'react';
import { Box, Flex } from '../CommonUI/Layout';
import { Button, TrustpilotSpinner } from '../CommonUI';
import { Text } from 'ilenia';
import { ShopComponent, PeriodComponent, ModalComponent, ProgressBarsComponent, PastOrdersSummary } from '../Components/EnhancedPastOrders';
import { connect } from 'react-redux';
import * as actions from '../Actions/PastOrders/index';
import * as tracking from '../Actions/analytics';
import { Settings, InvitationsSettings, PastOrdersStatus, ConfigurationScope, EmailImageType, TierFeature, EmailSentFrom, EmailType } from '../Model';
import { css, StyleSheet } from 'aphrodite';
import EmailExample from '../Components/Common/EmailExample';
import { Utils } from '../Utils';

interface EnhancedPastOrdersProps {
    productReviewsEnabled?: boolean;
    invitationSettings: InvitationsSettings;
    syncInProgress: boolean;
    isFetching?: boolean;
    status: PastOrdersStatus;
    pastOrders: any;
    configurationScopeTree: ConfigurationScope[];
    period: string;
    isFetchingPastOrdersSettings?: boolean;
    features: string[];
    fetchPastOrdersSettings: () => {};
    setRenderModal: (renderModal: boolean) => {};
    startSync: () => void;
    onProductReviewsSettingChange: (selector: Settings) => void;
    onSettingsSave: () => void;
    fetchStatus: () => void;
    track: (name: string, data: any) => void;
    pageView: () => void;
    sendPostMessage: (message: string | object) => void;
}

class EnhancedPastOrders extends React.Component<EnhancedPastOrdersProps> {
    private timer: NodeJS.Timeout | null = null;

    constructor(props: EnhancedPastOrdersProps) {
        super(props);

        props.pageView();
        props.fetchPastOrdersSettings();
        this.onSync = this.onSync.bind(this);
        this.sendPostMessage = this.sendPostMessage.bind(this);
    }

    public componentDidMount() {
        const { fetchStatus } = this.props;
        fetchStatus();
        this.timer = setInterval(() => {
            const { status } = this.props;
            if (status.inProgress) {
                fetchStatus();
            }
        }, 30000);
    }

    public componentWillUnmount() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    public renderPastOrderStatus() {
        const { status, invitationSettings, pastOrders } = this.props;

        if (status.inProgress) {
            return <ProgressBarsComponent />;
        } else if (pastOrders.showInitial) {
            return <EmailExample
                invitationSettings={invitationSettings}
                style={styles.emailPreview}
                imageType={EmailImageType.PastOrders}
                sentFrom={EmailSentFrom.PastOrders}
                templateType={EmailType.ServiceReviews}
            />;
        } else {
            return <PastOrdersSummary />;
        }
    }

    public render() {
        const { onProductReviewsSettingChange, onSettingsSave, configurationScopeTree, status } = this.props;

        const { isFetching, isFetchingPastOrdersSettings } = this.props;
        if ((isFetching && !status.inProgress) || isFetchingPastOrdersSettings) {
            return <TrustpilotSpinner active={true} height="520px" />;
        }

        return (
            <Box mt={0} className={css(styles.layout)}>
                <Flex flexDirection="row" alignItems="flex-start" justifyContent="flex-start">
                    <Box className={css(styles.settingsBox)} mt={0}>
                        <Flex>
                            <Box pl={20} mt={32} className={css(styles.title)}>
                                <Text id="enhancedPastOrders.title"/>
                            </Box>
                            <Box pl={20} mt={16} className={css(styles.info)}>
                                <Text id="enhancedPastOrders.note1"/>
                            </Box>
                            {
                                configurationScopeTree.length > 1
                                    ? <ShopComponent disabled={status.inProgress} />
                                    : null
                            }
                            <PeriodComponent disabled={status.inProgress} />
                            <ModalComponent
                                onProductReviewsSettingChange={onProductReviewsSettingChange}
                                onSettingsSave={onSettingsSave}
                                sendPostMessage={this.sendPostMessage}
                            />
                            <Button
                              id="syncButtonClick"
                              width={'unset'}
                              onClick={this.onSync}
                              disabled={status.inProgress}
                            >
                              <Text id="enhancedPastOrders.syncPastOrders"/>
                            </Button>
                        </Flex>
                    </Box>
                    <Box className={css(styles.secondColumn)}>
                    {
                        this.renderPastOrderStatus()
                    }
                    </Box>
                </Flex>
            </Box>
        );
    }

    private onSync() {
        const { productReviewsEnabled, setRenderModal, features } = this.props;

        if (features.indexOf(TierFeature.Reviews_ProductReviews) > -1 && !productReviewsEnabled) {
            setRenderModal(true);
        } else {
            const { track, startSync } = this.props;
            track('Integration Button Clicked', {
                context: 'Ecommscript integration app',
                location: window.location.pathname,
                placement: 'Bottom left corner',
                action: 'Sync past orders clicked',
                name: 'sync-past-orders-button',
            });
            startSync();
            this.sendPostMessage();
        }
    }

    private sendPostMessage() {
        const { period } = this.props;
        const periodInDays = Utils.convertToDays(period);
        if (periodInDays) {
            this.props.sendPostMessage(`sync:${periodInDays}`);
        } else {
            console.log(`Sync process did not started - ${period}`);
        }
    }
}

const mapStateToProps = (state: any) => ({
    productReviewsEnabled: state.PastOrders.productReviewsEnabled,
    invitationSettings: state.PastOrders.invitationSettings,
    syncInProgress: state.PastOrders.syncInProgress,
    status: state.PastOrders.status,
    isFetching: state.PastOrders.isFetching,
    pastOrders: state.PastOrders.pastOrders,
    period: state.PastOrders.period,
    configurationScopeTree: state.App.configurationScopeTree,
    isFetchingPastOrdersSettings: state.PastOrders.isFetchingPastOrdersSettings,
    features: state.PastOrders.features,
});

const mapDispatchToProps = (dispatch: any) => ({
    fetchPastOrdersSettings: () => dispatch(actions.fetchPastOrdersSettings()),
    setRenderModal: (renderModal: boolean) => dispatch(actions.setRenderModal(renderModal)),
    startSync: () => dispatch(actions.startSync()),
    fetchStatus: () => dispatch(actions.fetchStatus()),
    track: (name: string, data: any) => dispatch(tracking.track(name, data)),
    pageView: () => dispatch(tracking.pageView()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnhancedPastOrders);

const styles = StyleSheet.create({
    layout: {
    },
    settingsBox: {
        maxWidth: '600px',
        minHeight: '600px',
        borderRight: '1px solid rgb(223, 226, 229)',
    },
    optionalSeting: {
        paddingBottom: '16px',
    },
    greenText: {
        color: 'rgb(0, 182, 122)',
    },
    accordionDescription: {
        padding: '0px 22px',
    },
    title: {
        color: 'rgb(41, 41, 41)',
        fontSize: '20px',
        fontWeight: 'bold',
    },
    info: {
        color: 'rgb(102, 102, 102)',
        fontSize: '14px',
        fontWeight: 'normal',
        letterSpacing: '0px',
        lineHeight: '24px',
    },
    secondColumn: {
       marginTop: 0,
       padding: '32px 70px',
       maxWidth: '600px',
       minHeight: '600px',
    },
    emailPreview: {
        border: 'none',
        background: 'transparent !important',
    }
});



// WEBPACK FOOTER //
// ./src/Containers/EnhancedPastOrders.tsx